import moment from 'moment'

import * as types from 'constants/action_types/rank_tracker'
import { LoadingStates } from 'constants/api'
import {
  MY_PRODUCTS_SOURCE,
  COMPETITORS_SOURCE,
  NEW_ENTRY_SOURCE
} from 'constants/rank_tracker_v2'
import { RESET_RANK_TRACKER } from 'constants/action_types/rank_tracker'

import {
  mergeWithExistingProductConfiguration,
  mergeWithCleanProductConfiguration
} from './details'

export const INITIAL_STATE = {
  data: {
    activeCategory: {}, // Represents active tab
    keywordLists: [], // Use when adding a new product,
    orderDir: 'asc',
    orderBy: '',
    createCategoryName: '',
    selectedProductsIds: [],
    // Product Details
    product: null,
    dates: {
      startDate: moment().subtract(60, 'days'),
      endDate: moment()
    },
    rankKeywords: [],

    // ==== Rank configuration ======
    productSource: MY_PRODUCTS_SOURCE,
    keywordsSource: COMPETITORS_SOURCE,
    productSourceSelectedCountryCode: null,
    productSourceSelectedAsins: [],

    // **** AddProductStep ****
    runReverseAsinSearch: true,

    // for mwsProductSource, country code comes from:
    // amazonSellerAccountSelector.countryCode within globalData
    // see reducers/global.js => SELECT_AMAZON_SELLER_ACCOUNT
    mwsProductSource: {
      countryCode: null,
      selectedProducts: [],
      products: []
    },

    // enter new asin source
    newAsinProductSource: {
      countryCode: 'us',
      asin: ''
    },

    // **** AddKeywordsStep ****
    competitorAsins: [],

    // Select keyword lists
    selectedKeywordLists: [],
    competitorAsinsImportedKeywords: [],

    // Keyword bank
    /*
    Each keyword will have the following format:
    {
      id: 1,
      name: 'keyword',
      volume: 10,
      loading: false,
      failedToLoad: false
    }
    */
    keywordBank: [],
    // Contains keywords that should not be added to the keyword bank
    removedKeywords: [],

    // Annotations
    annotations: []
  },

  ui: {
    completedSections: [],
    keywordsFilters: [], // Each product has a table of keywords
    isCreateGroupModalOpen: false,
    isDeleteCategoryConfirmOpen: false,
    loadingRankTrackerListState: LoadingStates.initialLoad,
    loadingDeleteProducts: LoadingStates.initialLoad,
    loadingRankTrackerProduct: LoadingStates.initialLoad,
    loadingRankTrackerProductKeywords: LoadingStates.initialLoad,
    loadingDeleteKeyword: LoadingStates.initialLoad,
    loadingMyProducts: LoadingStates.initialLoad,
    loadingProductAnnotations: LoadingStates.initialLoad,
    loadingDeleteAnnotation: LoadingStates.initialLoad,
    loadingUpdateAnnotation: LoadingStates.initialLoad,
    deleteProductModalOpen: false,
    deleteKeywordModalOpen: false,
    // These actions are for displaying success/errors in the Rank Tracker Actions
    actions: {
      isComplete: null,
      isLoading: LoadingStates.initialLoad
    },
    column: 'name',
    direction: 'asc',
    newAsinProductSource: {
      productName: null,
      productImageUrl: null,
      hasValidationError: false,
      errorMessage: null,
      isValidAsin: false,
      isValidationPending: false
    },
    productPreviews: {
      isLoading: false,
      products: []
    }
  }
}

const mergeWithStepsCompletion = state => {
  const completedSections = []

  if (
    state.data.productSource === MY_PRODUCTS_SOURCE &&
    state.data.mwsProductSource.selectedProducts.length
  ) {
    completedSections.push(1)
  }

  if (state.data.productSource === NEW_ENTRY_SOURCE) {
    const { countryCode, asin } = state.data.newAsinProductSource

    if (countryCode && asin && state.ui?.newAsinProductSource?.isValidAsin) {
      completedSections.push(1)
    }
  }

  return {
    ...state,
    ui: {
      ...state.ui,
      completedSections
    }
  }
}

export function rankTrackerV2(state = INITIAL_STATE, action) {
  const newState = () => {
    switch (action.type) {
      case types.RANK_TRACKER_PENDING_STATUS:
        return {
          ...state,
          ui: {
            ...state.ui,
            [action.payload.prop]: LoadingStates.loading
          }
        }

      case types.RANK_TRACKER_COMPLETE_STATUS:
        return {
          ...state,
          ui: {
            ...state.ui,
            [action.payload.prop]: action.payload.success
              ? LoadingStates.loaded
              : LoadingStates.failed
          }
        }

      case types.RANK_TRACKER_RESET_STATUS:
        return {
          ...state,
          ui: {
            ...state.ui,
            [action.payload.prop]: LoadingStates.initialLoad
          }
        }

      case types.RANK_TRACKER_SET_NEW_CATEGORY_NAME:
        return {
          ...state,
          data: {
            ...state.data,
            createCategoryName: action.payload.categoryName
          }
        }

      // Set Active Cateogry
      case types.RANK_TRACKER_SET_ACTIVE_CATEGORY:
        return {
          ...state,
          data: {
            ...state.data,
            activeCategory: action.payload.category
          }
        }

      case types.RANK_TRACKER_UPDATE_DELETE_PRODUCT_MODAL_VISIBILITY:
        return {
          ...state,
          ui: {
            ...state.ui,
            deleteProductModalOpen: action.payload.visible
          }
        }

      case types.RANK_TRACKER_UPDATE_SELECTED_PRODUCTS:
        return {
          ...state,
          data: {
            ...state.data,
            selectedProductsIds: action.payload.ids
          }
        }

      case types.RANK_TRACKER_SET_SORTING_PARAMS: {
        const { column, direction } = action.payload
        return {
          ...state,
          ui: {
            ...state.ui,
            column,
            direction
          }
        }
      }

      // Keywords List
      case types.RANK_TRACKER_LOAD_KEYWORD_LISTS_PENDING_STATUS:
        return {
          ...state,
          ui: {
            ...state.ui,
            loadingRankTrackerListState: LoadingStates.loading
          }
        }

      case types.RANK_TRACKER_LOAD_KEYWORD_LISTS_COMPLETE:
        return {
          ...state,
          data: {
            ...state.data,
            keywordLists: action.payload.keywordLists
          }
        }

      case types.RANK_TRACKER_LOAD_KEYWORD_LISTS_COMPLETE_STATUS:
        return {
          ...state,
          ui: {
            ...state.ui,
            loadingRankTrackerListState: LoadingStates.loaded
          }
        }

      case types.RANK_TRACKER_TOGGLE_CREATE_CATEGORY_MODAL:
        return {
          ...state,
          data: {
            ...state.data,
            createCategoryName: ''
          },
          ui: {
            ...state.ui,
            isCreateGroupModalOpen: !state.ui.isCreateGroupModalOpen
          }
        }

      case types.RANK_TRACKER_TOGGLE_DELETE_CATEGORY_CONFIRMATION:
        return {
          ...state,
          ui: {
            ...state.ui,
            isDeleteCategoryConfirmOpen: !state.ui.isDeleteCategoryConfirmOpen
          }
        }

      case types.RANK_TRACKER_SET_STEP_COMPLETE: {
        const { stepId } = action.payload
        const oldSections = state.ui.completedSections
        // don't add the same step ID more than once
        const newSections = oldSections.includes(stepId)
          ? oldSections
          : [...oldSections, stepId]

        return {
          ...state,
          ui: {
            ...state.ui,
            completedSections: newSections
          }
        }
      }
      // Rank configuration
      case types.RANK_TRACKER_LOAD_SOURCE_PRODUCTS_COMPLETE: {
        const { products, source } = action.payload
        let selectedProducts = []
        if (
          source === 'mwsProductSource' &&
          state.data.productSourceSelectedAsins.length > 0
        ) {
          selectedProducts = products.filter(
            p => p.asin === state.data.productSourceSelectedAsins[0]
          )
          const { product } = state.data
          // If the user disabled or deleted the product, Array selectedProducts could be empty
          // To prevent bugs, fill selectedProducts with necessary product information from state.data.product
          if (
            selectedProducts.length === 0 &&
            product &&
            product.asin === state.data.productSourceSelectedAsins[0]
          ) {
            const selectedProduct = {
              amazon_image_url: product.imageUrl,
              asin: product.asin,
              sku: product.sku,
              title: product.name
            }
            selectedProducts.push(selectedProduct)
          }
        }
        return {
          ...state,
          data: {
            ...state.data,
            [source]: {
              ...state.data[source],
              products,
              selectedProducts
            }
          }
        }
      }

      case types.RANK_TRACKER_MY_PRODUCTS_SELECTION:
        return {
          ...state,
          data: {
            ...state.data,
            [action.payload.source]: {
              ...state.data[action.payload.source],
              selectedProducts: action.payload.products
            }
          }
        }

      case types.RANK_TRACKER_SET_SELECTED_KEYWORDS:
        return {
          ...state,
          data: {
            ...state.data,
            selectedKeywordLists: action.payload.keywords
          }
        }

      case types.RANK_TRACKER_UPDATE_KEYWORD_BANK:
        return {
          ...state,
          data: {
            ...state.data,
            keywordBank: action.payload.keywordBank
          }
        }

      // Details page
      case types.RANK_TRACKER_LOAD_PRODUCT: {
        const { product } = action.payload
        return {
          ...state,
          data: {
            ...state.data,
            product
          }
        }
      }

      case types.RANK_TRACKER_LOAD_PRODUCT_KEYWORDS: {
        const { rankKeywords } = action.payload
        return {
          ...state,
          data: {
            ...state.data,
            keywordBank: rankKeywords,
            rankKeywords
          }
        }
      }

      case types.RANK_TRACKER_SET_CHART_DATES: {
        const { startDate, endDate } = action.payload
        return {
          ...state,
          data: {
            ...state.data,
            dates: {
              startDate,
              endDate
            }
          }
        }
      }

      case types.RANK_TRACKER_LOAD_PRODUCT_CONFIGURATION: {
        const { product } = action.payload
        return mergeWithExistingProductConfiguration(state, product)
      }

      case types.RANK_TRACKER_RESET_PRODUCT_CONFIGURATION: {
        return mergeWithCleanProductConfiguration(state)
      }

      case types.RANK_TRACKER_REMOVE_KEYWORD: {
        const { selectedKeywordId } = action.payload
        const rankKeywords = state.data.rankKeywords.filter(
          keyword => keyword.id !== selectedKeywordId
        )
        return {
          ...state,
          data: {
            ...state.data,
            keywordBank: rankKeywords,
            rankKeywords
          }
        }
      }

      case types.RANK_TRACKER_TOGGLE_DELETE_KEYWORD_MODAL: {
        return {
          ...state,
          ui: {
            ...state.ui,
            deleteKeywordModalOpen: action.payload.open
          }
        }
      }

      case types.SET_MANUAL_ADD_PRODUCT_COUNTRY:
        return {
          ...state,
          data: {
            ...state.data,
            newAsinProductSource: {
              ...state.data.newAsinProductSource,
              countryCode: action.payload
            }
          }
        }

      case types.SET_MANUAL_ADD_PRODUCT_ASIN:
        return {
          ...state,
          data: {
            ...state.data,
            newAsinProductSource: {
              ...state.data.newAsinProductSource,
              asin: action.payload
            }
          }
        }

      case types.RANK_TRACKER_RESET_ASIN_VALIDATION:
        return {
          ...state,
          ui: {
            ...state.ui,
            newAsinProductSource: {
              ...state.ui.newAsinProductSource,
              productName: null,
              productImageUrl: null,
              hasValidationError: false,
              isValidAsin: false,
              errorMessage: null,
              isValidationPending: false
            }
          }
        }

      case types.RANK_TRACKER_ADD_ASIN_VALIDATION_PENDING_STATUS:
        return {
          ...state,
          ui: {
            ...state.ui,
            newAsinProductSource: {
              ...state.ui.newAsinProductSource,
              isValidationPending: true
            }
          }
        }

      case types.RANK_TRACKER_ADD_ASIN_PRODUCT_INVALID:
        return {
          ...state,
          ui: {
            ...state.ui,
            newAsinProductSource: {
              ...state.ui.newAsinProductSource,
              productName: null,
              productImageUrl: null,
              hasValidationError: true,
              isValidAsin: false,
              errorMessage: action.payload.message,
              isValidationPending: false
            }
          }
        }

      case types.RANK_TRACKER_ADD_ASIN_PRODUCT_COMPLETE:
        return {
          ...state,
          ui: {
            ...state.ui,
            newAsinProductSource: {
              ...state.ui.newAsinProductSource,
              productName: action.payload.name,
              productImageUrl: action.payload.imageUrl,
              hasValidationError: false,
              isValidAsin: true,
              errorMessage: null,
              isValidationPending: false
            }
          }
        }

      case types.RANK_TRACKER_SET_COMPETITOR_ASINS:
        return {
          ...state,
          data: {
            ...state.data,
            competitorAsins: action.payload || []
          }
        }

      case types.RANK_TRACKER_PENDING_PRODUCT_SEARCH_PREVIEW:
        return {
          ...state,
          ui: {
            ...state.ui,
            productPreviews: {
              ...state.productPreviews,
              isLoading: true,
              products: action.payload || []
            }
          }
        }

      case types.RANK_TRACKER_COMPLETE_PRODUCT_SEARCH_PREVIEW:
        return {
          ...state,
          ui: {
            ...state.ui,
            productPreviews: {
              ...state.productPreviews,
              isLoading: false,
              products: action.payload
            }
          }
        }

      case types.RANK_TRACKER_SET_COMPETITOR_ASINS_IMPORTED_KEYWORDS:
        return {
          ...state,
          data: {
            ...state.data,
            competitorAsinsImportedKeywords: action.payload.keywords || []
          }
        }

      case types.RANK_TRACKER_TOGGLE_REVERSE_ASIN_SEARCH:
        return {
          ...state,
          data: {
            ...state.data,
            runReverseAsinSearch: !state.data.runReverseAsinSearch
          }
        }

      case types.RANK_TRACKER_SET_PRODUCT_SOURCE:
        return {
          ...state,
          data: {
            ...state.data,
            productSource: action.payload
          }
        }

      case types.RANK_TRACKER_SET_MWS_COUNTRY_CODE:
        return {
          ...state,
          data: {
            ...state.data,
            mwsProductSource: {
              ...state.data.mwsProductSource,
              countryCode: action.payload
            }
          }
        }
      case types.RANK_TRACKER_SET_SELECTED_PRODUCT_DATA:
        return {
          ...state,
          data: {
            ...state.data,
            ...action.payload
          }
        }

      case types.RANK_TRACKER_SET_KEYWORDS_SOURCE:
        return {
          ...state,
          data: {
            ...state.data,
            keywordsSource: action.payload
          }
        }

      case types.RANK_TRACKER_UPDATE_REMOVED_KEYWORDS_LIST: {
        return {
          ...state,
          data: {
            ...state.data,
            removedKeywords: action.payload.removedKeywords
          }
        }
      }
      case types.RANK_TRACKER_SET_PRODUCT_ANNOTATIONS: {
        return {
          ...state,
          data: {
            ...state.data,
            annotations: action.payload.annotations
          }
        }
      }
      case RESET_RANK_TRACKER:
        return {
          ...INITIAL_STATE
        }
      default:
        return state
    }
  }
  return mergeWithStepsCompletion(newState())
}
